import {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useApiTranslations} from '../../../../context/ApiTranslationsContext';
import {LicenseTemplateController} from '../../../../api/license/license-template.controller';
import {FeatureTemplate} from '../../../../model/license/feature-template.model';
import {SortingUtils} from '../../../../utils/sorting.utils';
import {LicenseTemplatePageHooks} from '../../../license/template/state/license-template-page-hooks';
import UpdateFeatureTemplateEntry = LicenseTemplateController.UpdateFeatureTemplateEntry;

export type CmsFeatureTemplateMassiveAction = 'no_action' | 'update_feature_template'

export type CmsFeatureTemplatePageState = {
  featureTemplates: FeatureTemplate[]
  massiveAction: CmsFeatureTemplateMassiveAction
  isSaveDisabled: boolean,

  mainTableRows: FeatureTemplate[]
  getChildRows: (row: FeatureTemplate) => FeatureTemplate[];
  handleRowToggle: (id: string) => void
  isRowOpen: (id: string) => boolean
  areAllExpanded: boolean
  toggleExpandAll: () => void
  setMassiveAction: (action: CmsFeatureTemplateMassiveAction) => void
  updateFeatureTemplates: () => Promise<void>
  onFeatureTemplateUpdated: (featureTemplate: FeatureTemplate) => void

  translate: (key: string) => string
  translateLocal: (key: string) => string
}

export const useCmsFeatureTemplatePageState = (): CmsFeatureTemplatePageState => {
  const {t} = useTranslation()
  const {translate: apiTranslate} = useApiTranslations()
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({});
  const [massiveAction, setMassiveAction] = useState<CmsFeatureTemplateMassiveAction>('no_action')
  const [modifiedFeatureTemplates, setModifiedFeatureTemplates] = useState<string[]>([])

  const [featureTemplates, setFeatureTemplates] = LicenseTemplatePageHooks.useFeatureTemplates()

  const refreshFeatureTemplates = async () => {
    setFeatureTemplates(await LicenseTemplatePageHooks.fetchFeatureTemplates())
  }

  const onMassiveActionChanged = async (action: CmsFeatureTemplateMassiveAction) => {
    if (action === 'no_action') {
      await refreshFeatureTemplates()
      setModifiedFeatureTemplates([])
    }
    setMassiveAction(action)
  }

  const updateFeatureTemplateParameters = (featureTemplate: FeatureTemplate) => {
    let isUpdated = false
    const updated = featureTemplates.map(it => {
      if(it.id === featureTemplate.id) {
        it = featureTemplate
        isUpdated = true;
      }
      return it;
    })
    if(isUpdated) {
      setModifiedFeatureTemplates([...modifiedFeatureTemplates, featureTemplate.id])
    }
    setFeatureTemplates(updated)
  }

  const updateFeatureTemplates = async () => {
    const values: UpdateFeatureTemplateEntry[] = featureTemplates
      .filter(it => modifiedFeatureTemplates.includes(it.id))
      .map(it => ({
        featureTemplateId: it.id,
        activationCost: it.activationCost,
        enabled: it.enabled,
        deliveryDate: it.deliveryDate,
        version: it.version
      }))

    await LicenseTemplateController.updateFeatureTemplates(values)
    await onMassiveActionChanged('no_action')
  }

  const moduleParentIds = Array.from(new Set(featureTemplates.map(it => `module-header-${it.featureModule}`)));
  const areAllExpanded = moduleParentIds.length > 0 && moduleParentIds.every(id => openRows[id]);

  return {
    featureTemplates: featureTemplates,
    massiveAction: massiveAction,
    isSaveDisabled: modifiedFeatureTemplates.length === 0,
    mainTableRows: SortingUtils.sortFeatureTemplatesByModulePriority(SortingUtils.createModuleParents(featureTemplates)),
    onFeatureTemplateUpdated: updateFeatureTemplateParameters,
    setMassiveAction: onMassiveActionChanged,
    updateFeatureTemplates: updateFeatureTemplates,
    getChildRows: (parent) => featureTemplates.filter(it => it.featureModule === parent.featureModule),
    isRowOpen: (id) => openRows[id] || false,
    handleRowToggle: (id) => {
      setOpenRows((prev) => ({ ...prev, [id]: !prev[id] }));
    },
    areAllExpanded: areAllExpanded,
    toggleExpandAll: () => {
      if (areAllExpanded) {
        setOpenRows({});
      } else {
        const allOpen: Record<string, boolean> = {};
        moduleParentIds.forEach(id => { allOpen[id] = true; });
        setOpenRows(allOpen);
      }
    },
    translate: (key) => apiTranslate(key) || t(key),
    translateLocal: (key) => t(`cms-feature-template-page.${key}`),
  }
}
